import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  User,
  Calendar,
  FileText,
  DollarSign,
  Loader2,
  Plus,
  MoreHorizontal,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  ArrowRight,
  Copy,
  Clock,
  Users,
  Package,
  Download,
} from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { QuoteItemModal } from '@/components/travel/quotes/quote-item-modal';
import { QuotePDF } from '@/components/travel/quotes/quote-pdf';
import { ConvertToBookingModal } from '@/components/travel/quotes/convert-to-booking-modal';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-purple-100 text-purple-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  DECLINED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-orange-100 text-orange-700',
  CONVERTED: 'bg-cyan-100 text-cyan-700',
};

const statusLabelKeys: Record<string, string> = {
  DRAFT: 'statusLabels.DRAFT',
  SENT: 'statusLabels.SENT',
  VIEWED: 'statusLabels.VIEWED',
  ACCEPTED: 'statusLabels.ACCEPTED',
  DECLINED: 'statusLabels.DECLINED',
  EXPIRED: 'statusLabels.EXPIRED',
  CONVERTED: 'statusLabels.CONVERTED',
};

type QuoteStatus = 'DRAFT' | 'SENT' | 'VIEWED' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'CONVERTED';
type ServiceType = 'ACCOMMODATION' | 'TRANSPORT' | 'ACTIVITY' | 'MEAL' | 'GUIDE' | 'INSURANCE' | 'TRANSFER' | 'OTHER';

type QuoteItem = {
  id: string;
  packageId: string | null;
  itemType: ServiceType;
  name: string;
  description: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  sortOrder: number;
  package?: {
    id: string;
    name: string;
    code: string;
  } | null;
};

export function QuoteDetailPage() {
  const { t } = useTranslation('quotes');
  const { id } = useParams();
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);

  const { data: quote, isLoading, error } = trpc.quote.byId.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const updateStatusMutation = trpc.quote.updateStatus.useMutation({
    onSuccess: () => {
      utils.quote.byId.invalidate(id);
    },
  });

  const removeItemMutation = trpc.quote.removeItem.useMutation({
    onSuccess: () => {
      utils.quote.byId.invalidate(id);
    },
  });

  const duplicateMutation = trpc.quote.duplicate.useMutation({
    onSuccess: (newQuote) => {
      window.location.href = `/quotes/${newQuote.id}`;
    },
  });


  const handleStatusChange = (newStatus: QuoteStatus) => {
    if (id) {
      updateStatusMutation.mutate({ id, status: newStatus });
    }
  };

  const handleRemoveItem = (itemId: string) => {
    if (confirm(t('confirmRemoveItem'))) {
      removeItemMutation.mutate(itemId);
    }
  };

  const handleDuplicate = () => {
    if (id) {
      duplicateMutation.mutate(id);
    }
  };

  const handleConvertToBooking = () => {
    setIsConvertModalOpen(true);
  };

  const handleConvertSuccess = (bookingId: string) => {
    window.location.href = `/bookings/${bookingId}`;
  };

  const handleAddItem = () => {
    setIsItemModalOpen(true);
  };

  const handleDownloadPdf = async () => {
    if (!quote) return;

    setIsGeneratingPdf(true);
    try {
      const pdfData = {
        ...quote,
        travelStartDate: quote.travelStartDate ? String(quote.travelStartDate) : null,
        travelEndDate: quote.travelEndDate ? String(quote.travelEndDate) : null,
        validUntil: String(quote.validUntil),
        createdAt: String(quote.createdAt),
        agent: quote.agent ?? { firstName: 'N/A', lastName: '', email: '' },
        customer: {
          ...quote.customer,
          email: quote.customer.email ?? '',
        },
      };

      const blob = await pdf(<QuotePDF quote={pdfData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quote.quoteNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(t('pdfError'));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{t('loadingQuote')}</span>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="space-y-4">
        <Link
          to="/travel/quotes"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('backToQuotes')}
        </Link>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('quoteNotFound')}</p>
          <p className="text-sm">{error?.message || t('quoteNotExist')}</p>
        </div>
      </div>
    );
  }

  const isExpired = new Date(quote.validUntil) < new Date() && quote.status !== 'CONVERTED';

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/travel/quotes"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Quotes
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{quote.quoteNumber}</span>
            <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[quote.status]}`}>
              {t(statusLabelKeys[quote.status] || quote.status)}
            </span>
            {isExpired && quote.status !== 'EXPIRED' && (
              <span className="bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                {t('expiredLabel')}
              </span>
            )}
            <span className="text-xs text-muted-foreground">v{quote.version}</span>
          </div>
          <h1 className="text-2xl font-bold">
            {t('quoteFor', { name: `${quote.customer.firstName} ${quote.customer.lastName}` })}
          </h1>
          <p className="text-muted-foreground">{quote.customer.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownloadPdf} disabled={isGeneratingPdf}>
            {isGeneratingPdf ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {t('downloadPdf')}
          </Button>
          <Button variant="outline" onClick={handleDuplicate}>
            <Copy className="mr-2 h-4 w-4" />
            {t('duplicate')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {quote.status === 'DRAFT' && (
                <DropdownMenuItem onClick={() => handleStatusChange('SENT')}>
                  <Send className="mr-2 h-4 w-4" />
                  {t('markAsSent')}
                </DropdownMenuItem>
              )}
              {quote.status === 'SENT' && (
                <>
                  <DropdownMenuItem onClick={() => handleStatusChange('VIEWED')}>
                    <FileText className="mr-2 h-4 w-4" />
                    {t('markAsViewed')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange('ACCEPTED')}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {t('markAsAccepted')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange('DECLINED')}>
                    <XCircle className="mr-2 h-4 w-4" />
                    {t('markAsDeclined')}
                  </DropdownMenuItem>
                </>
              )}
              {quote.status === 'VIEWED' && (
                <>
                  <DropdownMenuItem onClick={() => handleStatusChange('ACCEPTED')}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {t('markAsAccepted')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange('DECLINED')}>
                    <XCircle className="mr-2 h-4 w-4" />
                    {t('markAsDeclined')}
                  </DropdownMenuItem>
                </>
              )}
              {quote.status === 'ACCEPTED' && (
                <DropdownMenuItem onClick={handleConvertToBooking}>
                  <ArrowRight className="mr-2 h-4 w-4" />
                  {t('convertToBooking')}
                </DropdownMenuItem>
              )}
              {quote.status !== 'EXPIRED' && quote.status !== 'CONVERTED' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleStatusChange('EXPIRED')}>
                    <Clock className="mr-2 h-4 w-4" />
                    {t('markAsExpired')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Booking Link */}
      {quote.booking && (
        <div className="border border-green-200 bg-green-50 p-4">
          <p className="text-sm text-green-700">
            {t('convertedMessage', { id: '' })}{' '}
            <Link to={`/travel/bookings/${quote.booking.id}`} className="font-medium underline">
              {quote.booking.bookingReference}
            </Link>
          </p>
        </div>
      )}

      {/* Quote Item Modal */}
      {isItemModalOpen && (
        <QuoteItemModal
          open={isItemModalOpen}
          onOpenChange={setIsItemModalOpen}
          quoteId={quote.id}
          onSuccess={() => utils.quote.byId.invalidate(id)}
        />
      )}

      {/* Convert to Booking Modal */}
      {quote && (
        <ConvertToBookingModal
          open={isConvertModalOpen}
          onOpenChange={setIsConvertModalOpen}
          quote={{
            id: quote.id,
            quoteNumber: quote.quoteNumber,
            totalAmount: Number(quote.totalAmount),
            currency: quote.currency,
            travelStartDate: quote.travelStartDate,
            travelEndDate: quote.travelEndDate,
            customer: {
              firstName: quote.customer.firstName,
              lastName: quote.customer.lastName,
              email: quote.customer.email ?? '',
            },
          }}
          onSuccess={handleConvertSuccess}
        />
      )}

      {/* Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quote Info Card */}
          <div className="border border-border bg-card p-6">
            <h2 className="mb-4 font-semibold">{t('quoteDetails')}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('travelDatesLabel')}</p>
                  <p className="font-medium">
                    {quote.travelStartDate && quote.travelEndDate ? (
                      <>
                        {new Date(quote.travelStartDate).toLocaleDateString()} -{' '}
                        {new Date(quote.travelEndDate).toLocaleDateString()}
                      </>
                    ) : (
                      <span className="text-muted-foreground">{t('notSetLabel')}</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('validUntilLabel')}</p>
                  <p className={`font-medium ${isExpired ? 'text-red-600' : ''}`}>
                    {new Date(quote.validUntil).toLocaleDateString()}
                    {isExpired && t('expiredSuffix')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('travelersLabel')}</p>
                  <p className="font-medium">
                    {quote.adults} {t('adultsLabel')}
                    {quote.children > 0 && `, ${quote.children} ${t('childrenLabel')}`}
                    {quote.infants > 0 && `, ${quote.infants} ${t('infantsLabel')}`}
                  </p>
                </div>
              </div>
              {quote.agent && (
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('agent')}</p>
                    <p className="font-medium">
                      {quote.agent.firstName} {quote.agent.lastName}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {quote.notes && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">{t('notes')}</p>
                <p className="text-sm whitespace-pre-wrap">{quote.notes}</p>
              </div>
            )}
            {quote.internalNotes && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">{t('internalNotesLabel')}</p>
                <p className="text-sm whitespace-pre-wrap bg-yellow-50 p-2">{quote.internalNotes}</p>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('lineItems')}
              </h2>
              {quote.status === 'DRAFT' && (
                <Button variant="outline" size="sm" onClick={handleAddItem}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('addItem')}
                </Button>
              )}
            </div>
            {quote.items && quote.items.length > 0 ? (
              <div className="space-y-3">
                {quote.items.map((item: QuoteItem) => (
                  <div key={item.id} className="border border-border p-4 group">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="bg-muted px-2 py-0.5 text-xs">
                            {item.itemType.replace('_', ' ')}
                          </span>
                          {item.package && (
                            <Link
                              to={`/travel/packages/${item.package.id}`}
                              className="flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              <Package className="h-3 w-3" />
                              {item.package.code}
                            </Link>
                          )}
                        </div>
                        <h3 className="font-medium">{item.name}</h3>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        )}
                      </div>
                      <div className="text-right flex items-start gap-2">
                        <div>
                          <p className="font-medium">{formatCurrency(item.totalPrice)}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.quantity} x {formatCurrency(item.unitPrice)}
                          </p>
                        </div>
                        {quote.status === 'DRAFT' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{t('noItems')}</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Card */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold flex items-center gap-2 mb-4">
              <User className="h-5 w-5" />
              {t('customerLabel')}
            </h2>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">
                  {quote.customer.firstName} {quote.customer.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{quote.customer.email}</p>
              </div>
            </div>
            {quote.customer.phone && (
              <p className="text-sm text-muted-foreground mb-2">
                {t('phone')}{quote.customer.phone}
              </p>
            )}
            <Link
              to={`/travel/cari/accounts/customer/${quote.customer.id}`}
              className="text-sm text-primary hover:underline"
            >
              {t('viewCustomerProfile')} →
            </Link>
          </div>

          {/* Pricing Summary */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5" />
              {t('pricingSummary')}
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('subtotal')}</span>
                <span>{formatCurrency(quote.subtotal)}</span>
              </div>
              {quote.discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('discountLabel')}
                    {quote.discountPercent && ` (${quote.discountPercent}%)`}
                  </span>
                  <span className="text-green-600">-{formatCurrency(quote.discountAmount)}</span>
                </div>
              )}
              {quote.taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('tax')}</span>
                  <span>{formatCurrency(quote.taxAmount)}</span>
                </div>
              )}
              <div className="border-t pt-3 flex justify-between font-medium text-lg">
                <span>{t('total')}</span>
                <span>{formatCurrency(quote.totalAmount)}</span>
              </div>
              <p className="text-xs text-muted-foreground text-right">
                {t('currencyLabel')}{quote.currency}
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5" />
              {t('timeline')}
            </h2>
            <div className="space-y-3">
              <div className="text-sm">
                <p className="text-muted-foreground">{t('created')}</p>
                <p>{new Date(quote.createdAt).toLocaleString()}</p>
              </div>
              {quote.sentAt && (
                <div className="text-sm">
                  <p className="text-muted-foreground">{t('sent')}</p>
                  <p>{new Date(quote.sentAt).toLocaleString()}</p>
                </div>
              )}
              {quote.viewedAt && (
                <div className="text-sm">
                  <p className="text-muted-foreground">{t('viewed')}</p>
                  <p>{new Date(quote.viewedAt).toLocaleString()}</p>
                </div>
              )}
              {quote.acceptedAt && (
                <div className="text-sm">
                  <p className="text-muted-foreground">{t('accepted')}</p>
                  <p>{new Date(quote.acceptedAt).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
